import {
  onBeforeMount,
  onMounted,
  onActivated,
  onDeactivated,
  onBeforeUnmount,
  getCurrentInstance,
  nextTick,
  watch,
  computed,
  isRef,
  provide,
  inject,
} from 'vue';
import {
  isArray,
  filter,
  isString,
  isEmpty,
  get,
  set,
  isUndefined,
  isObject,
} from 'lodash-es';
import debug from 'debug';
import { useHelpers } from './useHelpers';
import { useComponentState } from './useComponentState';
import { useForms } from './useForms';
import { useDataLoader } from './useDataLoader';
import {
  injectPageLoadTracker,
  usePageLoadTracker,
} from './usePageLoadTracker';
import type {
  IUserComponent,
  LoadReporter,
  PageLoadTracker,
  SetDataOptions,
  UserComponentOptions,
  UserComponentProps,
} from './types';
import { FormInstance } from '../schemas';

const log = debug('itp:core:useUserComponent');

export function useUserComponent(
  props: UserComponentProps & Record<string, any>,
  options: UserComponentOptions,
): IUserComponent {
  const instance = getCurrentInstance();
  const context = instance?.proxy;

  const helpers = useHelpers();
  const state = useComponentState(props);

  state.componentMetadata.value = options.metadata;
  state.ux.value = options.ux;

  if (instance) {
    state.$uid.value = instance.type.name || 'Anonymous';
  }

  if (props.modal) {
    state.isModalComponent.value = true;
  }
  if (props.modalName) {
    state.modalName.value = props.modalName;
  }

  const inheritedInsideModal = inject<boolean>('itp-inside-modal', false);
  const isInsideModal = !!props.modal || inheritedInsideModal;
  provide('itp-inside-modal', isInsideModal);

  const friendlyName = () => state.modalName.value || state.$uid.value;

  const {
    forms,
    registerForm: localRegisterForm,
    unregisterForm: localUnregisterForm,
  } = useForms(friendlyName);

  const isRoot = ['page', 'modal'].includes(options.metadata.kind);

  if (isRoot) {
    provide('itp-root-register-form', localRegisterForm);
    provide('itp-root-unregister-form', localUnregisterForm);
  }

  const ux = options.ux || {};

  const loadTracker: PageLoadTracker | null = isRoot
    ? usePageLoadTracker({ name: friendlyName() })
    : injectPageLoadTracker();

  const pageWaitsForThis =
    !!loadTracker &&
    (isRoot ||
      (ux.blocksPageLoad !== false && ux.loadingPlaceholder !== 'none'));

  const loadReporter: LoadReporter | null = !pageWaitsForThis
    ? null
    : isRoot
      ? loadTracker!.pageReporter
      : loadTracker!.waitFor(friendlyName());

  const rootRegisterForm = isRoot
    ? null
    : inject<((name: string, form: FormInstance) => void) | null>(
        'itp-root-register-form',
        null,
      );
  const rootUnregisterForm = isRoot
    ? null
    : inject<((form: FormInstance) => void) | null>(
        'itp-root-unregister-form',
        null,
      );

  const registerForm = (name: string, form: FormInstance) => {
    localRegisterForm(name, form);
    if (!isRoot && rootRegisterForm) {
      rootRegisterForm(name, form);
    }
  };

  const unregisterForm = (form: FormInstance) => {
    localUnregisterForm(form);
    if (!isRoot && rootUnregisterForm) {
      rootUnregisterForm(form);
    }
  };

  provide('itp-register-form', registerForm);
  provide('itp-unregister-form', unregisterForm);

  const dataLoaderState = {
    ...state,
    ...(options.dataRefs || {}),
  };

  const dataLoader = useDataLoader({
    state: dataLoaderState,
    helpers,
    friendlyName,
    dataSpecs: options.dataSpecs,
    loaders: options.loaders,
    context,
    ux: options.ux,
    onReloadStart: () =>
      loadTracker && (isRoot || loadTracker.reloading.value)
        ? loadTracker.reloadStarted()
        : undefined,
  });

  if (loadReporter) {
    watch(
      state.loaded,
      (isLoaded) => {
        if (isLoaded) {
          loadReporter.reportLoaded();
        }
      },
      { flush: 'post' },
    );

    const reportFailure = (err: any) => {
      if (ux.errorHandling === 'local') {
        loadReporter.reportLoaded();
      } else {
        loadReporter.reportFailed(err);
      }
    };

    watch(
      state.error,
      (err) => {
        if (err) {
          reportFailure(err);
        }
      },
      { flush: 'post' },
    );

    onMounted(() => {
      if (state.loaded.value) {
        loadReporter.reportLoaded();
      }
    });

    onBeforeUnmount(() => loadReporter.stopWaiting());

    watch(
      () => loadTracker!.retryCount.value,
      async () => {
        try {
          await dataLoader.loadData(null, { initial: true });
          await nextTick();
          loadReporter.reportLoaded();
        } catch (err) {
          reportFailure(err);
        }
      },
    );
  }

  const setData = (path: string, value: any, opts?: SetDataOptions): void => {
    const pathParts = path.split('.');
    const rootName = pathParts[0];
    const dataRef = options.dataRefs?.[rootName];

    if (dataRef && isRef(dataRef)) {
      if (pathParts.length === 1) {
        dataRef.value = value;
      } else {
        const nestedPath = pathParts.slice(1).join('.');
        const refValue = dataRef.value;
        if (isUndefined(get(refValue, nestedPath))) {
          console.warn(
            `setData: ${path} path is undefined on component ${friendlyName()}. ` +
              'This might be a problem if this value is expected to be reactive.',
          );
        }
        if (isObject(refValue)) {
          set(refValue as object, nestedPath, value);
        }
      }
    } else {
      const stateRef = (state as any)[rootName];
      if (stateRef && isRef(stateRef)) {
        if (pathParts.length === 1) {
          stateRef.value = value;
        } else {
          const nestedPath = pathParts.slice(1).join('.');
          const refValue = stateRef.value;
          if (isObject(refValue)) {
            set(refValue as object, nestedPath, value);
          }
        }
      } else if (
        pathParts.length > 1 &&
        rootName in props &&
        isObject(props[rootName])
      ) {
        const nestedPath = pathParts.slice(1).join('.');
        set(props[rootName] as object, nestedPath, value);
      } else {
        console.warn(
          `setData: ${path} could not be resolved on component ${friendlyName()}.`,
        );
      }
    }

    log('[%s] setData %s := %o', friendlyName(), path, value);

    if (!opts || !opts.noTouchLastLoaded) {
      dataLoader.touchLastLoaded();
    }
  };

  const documentTitle = computed(() => {
    if (state.pageDocument.value) {
      const result = state.pageDocument.value.title;
      if (isArray(result)) {
        return filter(result, (p) => isString(p) && !isEmpty(p)).join(' | ');
      }
      return result as string;
    }
    return undefined;
  });

  const ROUTE_TAB_LOADING_PLACEHOLDER = '..';

  const isRoutePage =
    !isInsideModal &&
    !!helpers.$route?.matched.some(
      (record) => record.components?.default === instance?.type,
    );
  const pageRoutePath = isRoutePage ? helpers.$route.fullPath : null;
  let isDisplayed = true;

  const setRouteTabTitle = (title: string) => {
    log("updateRouteTab [%s] to '%s'", friendlyName(), title);

    if (state.routeTab.value) {
      state.routeTab.value.title = title;
      state.routeTab.value.tips = title;
      state.routeTab.value.icon = state.pageDocument.value?.icon;
    } else {
      state.routeTab.value = {
        title,
        tips: title,
        icon: state.pageDocument.value?.icon,
      };
    }
  };

  const updateRouteTab = () => {
    const title = documentTitle.value;

    if (title) {
      setRouteTabTitle(title);
      window.dispatchEvent(
        new CustomEvent('itp:routeTab', {
          detail: {
            path: pageRoutePath,
            title,
            icon: state.pageDocument.value?.icon,
          },
        }),
      );
    }
  };

  const documentTitleOwner = Symbol('itp-document-title-owner');

  const updateHeaders = () => {
    if (!pageRoutePath) {
      return;
    }

    if (state.loaded.value && documentTitle.value) {
      if (isDisplayed) {
        helpers.$app.value.documentTitle.set(
          documentTitle.value,
          documentTitleOwner,
        );
      }
      updateRouteTab();
    } else if (!state.loaded.value) {
      setRouteTabTitle(ROUTE_TAB_LOADING_PLACEHOLDER);
    }
  };

  watch(state.loaded, () => {
    updateHeaders();
  });

  watch(
    state.pageDocument,
    () => {
      updateHeaders();
    },
    { deep: true },
  );

  const reportInitialLoadError = (error: any) => {
    log('[%s] error loading data:', friendlyName(), error);
    const useGlobalModal = options.ux && options.ux.errorHandling === 'global';
    if (useGlobalModal) {
      props.componentInitErrorCallback?.(error);
    }
  };

  const startInitialLoad = () => {
    try {
      dataLoader
        .loadData(null, { initial: true })
        .catch(reportInitialLoadError);
    } catch (error) {
      reportInitialLoadError(error);
    }
  };

  // "created() { ... this.loadData(...) }"
  onBeforeMount(() => {
    if (helpers.$app.value.ready) {
      startInitialLoad();
    } else {
      helpers.$app.value
        .waitForReady()
        .then(startInitialLoad)
        .catch(reportInitialLoadError);
    }
  });

  onMounted(() => {
    log('[%s] Component mounted', friendlyName());
    updateHeaders();
  });

  onActivated(() => {
    log('[%s] Component activated', friendlyName());
    isDisplayed = true;
    updateHeaders();
  });

  onDeactivated(() => {
    isDisplayed = false;
  });

  onBeforeUnmount(() => {
    if (state.isModalComponent.value || state.routeTab.value) {
      helpers.$app.value.documentTitle.reset(documentTitleOwner);
    }
    // unsubscribeFromGlobalEvents(); // TODO: unsub
  });

  watch(
    () => props.reload,
    (_reload, previousReload) => {
      const alreadyLoading = state.loading.value || state.reloading.value;
      if (
        previousReload != null &&
        !alreadyLoading &&
        state.canLoadData.value
      ) {
        dataLoader.loadData().catch(() => {});
      }
    },
    { flush: 'post' },
  );

  return {
    ...helpers,
    ...state,
    forms,
    registerForm,
    unregisterForm,
    ...dataLoader,
    friendlyName,
    documentTitle,
    updateHeaders,
    setData,
  };
}
